import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import { INewUser } from '../../components/forms/AddUserForm/types'
import { IUserProfile } from '../../pages/UsersPage/constants'
import usersService from '../../services/usersService'
import actions from '../users/actions'
import { IUsersState } from './types'
import { INVESTIGATOR, DESIGNATED_RECEIVER } from '../../utils/userRoleTypeUtils'

import { error } from '../../utils/alertUtils';

const initialState: IUsersState = {
  users: [],
  organizationUsers: [],
}

export const resetUserPassword = createAsyncThunk(
  actions.RESET_USER_PASSWORD,
  async (payload: { userId: number }, { rejectWithValue }) => {
    try {
      const res = await usersService.resetUsersPassword(payload.userId)
      return res
    } catch (err) {
      error('Reset password failed. Please try again.');
      return rejectWithValue('Reset password failed')
    }
  },
)

export const deactivateUser = createAsyncThunk(
  actions.DEACTIVATE_USER,
  async (payload: { userId: number }, { rejectWithValue }) => {
    try {
      const res = await usersService.deactivateUser(payload.userId)
      return res
    } catch (err) {
      if (err instanceof Error) {
        error('Deactivate user failed. Please try again.')
        return rejectWithValue('Deactivate user failed')
      }
    }
  },
)

export const addNewUser = createAsyncThunk(
  actions.POST_NEW_USER,
  async (payload: { newUser: INewUser }, { rejectWithValue, dispatch }) => {
    try {
      // console.log('payload.newUser', payload.newUser)
      const res = await usersService.postNewUser(payload.newUser)
      dispatch(getOrganizationUsers({ searchValue: '',  excludeLoggedUser: true, onlyAssignable: false}))
      return res
    } catch (err) {
        if (err instanceof Error) {
        error('Error while adding new user. User with this email already exist!')
        return rejectWithValue('Adding new user failed')
      }
    }
  },
)
export const patchUser = createAsyncThunk(
  actions.PATCH_USER,
  async (payload: { newUser: any; userId: number }, { rejectWithValue, dispatch }) => {
    try {
      // console.log('payload.userId', payload.userId)

      // console.log('payload.newUser', payload.newUser)

      const res = await usersService.patchUser(payload.newUser, payload?.userId)
      dispatch(getOrganizationUsers({ searchValue: '',  excludeLoggedUser: true, onlyAssignable: false}))
      return res
    } catch (err) {
      if (err instanceof Error) {
        error('Adding new user failed. Please try again.')
        return rejectWithValue('Adding new user failed')
      }
    }
  },
)

export const patchUserProfile = createAsyncThunk(
  actions.PATCH_USER_PROFILE,
  async (payload: { userData: any; userId: number }, { rejectWithValue, dispatch }) => {
    try {
      // console.log('patchUserProfile payload.userData', payload.userData)

      const res = await usersService.patchUserProfile(payload.userData, payload?.userId)

      // console.log('patchUserProfile res', res)

      dispatch(getOrganizationUsers({ searchValue: '',  excludeLoggedUser: true, onlyAssignable: false }))
      return res
    } catch (err) {
      if (err instanceof Error) {
        error('Adding new user failed. Please try again.')
        return rejectWithValue('Adding new user failed')
      }
    }
  },
)

// onlyAssignable - INVESTIGATORS, DESIGNATED_RECEIVERS
export const getOrganizationUsers = createAsyncThunk(
  actions.GET_ORGANIZATION_USERS,
  async (payload: { searchValue: string, excludeLoggedUser: boolean, onlyAssignable: boolean }, { rejectWithValue }) => {
    try {
      const res = await usersService.getAllUsersInOrganization(payload.excludeLoggedUser)
      
      let filteredData = !payload.searchValue ? res.data : res.data.filter(
        (userProfile: IUserProfile) => 
          userProfile.user?.email?.toLowerCase().includes(payload.searchValue.toLowerCase()) ||
          userProfile.user?.first_name?.toLowerCase().includes(payload.searchValue.toLowerCase()) ||
          userProfile.user?.last_name?.toLowerCase().includes(payload.searchValue.toLowerCase())  
      )

      if(payload.onlyAssignable) {  
        filteredData = filteredData.filter((userProfile: IUserProfile) => {
          return userProfile.roles?.some((role: { text: string }) => role.text === INVESTIGATOR.value || role.text === DESIGNATED_RECEIVER.value)
          //.role === 'INVESTIGATOR' || user.role === 'DESIGNED_RECEIVER'  
        })
      }

      return filteredData.map((user: { access: any; role: any }) => {
        return {
          ...user,
          access_role: user?.access ?? undefined,
          case_role: user?.role ?? undefined,
        }
      })
    } catch (err) {
      if (err instanceof Error) {
        error('Failed to fetch all users. Please try again')
        return rejectWithValue('Failed to fetch all users')
      }
    }
  },
)

const usersSlice = createSlice({
  name: 'users',
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder.addCase(getOrganizationUsers.fulfilled, (state, { payload }) => {
      state.organizationUsers = payload
    })
  },
})

export default usersSlice.reducer
